import { useEffect, useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import i18n from '@/i18n';
import { useAuthStore } from '@/stores/authStore';
import { isAccountInactive } from '@/lib/accountActivation';
import { useNotificationStore } from '@/stores/notificationStore';
import { useBackgroundTaskStore } from '@/stores/backgroundTaskStore';
import { useProcessingOverlayStore } from '@/stores/processingOverlayStore';
import { callApi } from '@/hooks/useFetch';
import { openNotificationLink } from '@/utils/notificationLink';
import { tPath } from '@/utils/paths';
import { toast } from '@/lib/toast';
import type {
  Notification,
  NotificationType,
  NotificationListResponse,
  NotificationPollResponse,
  MarkReadResponse,
  UnreadCountResponse,
} from '@/types/notification';

// Backend-authored action_url patterns this hook knows how to react to —
// narrow on purpose (matches what's asked) rather than a generic
// URL-to-query-key system. Add a pattern here when another page needs the
// same "refresh if I'm already looking at it" treatment.
const STARTUP_PATH_RE = /^\/startups\/([^/]+)$/;

// If a just-arrived notification points at the page the user is already on,
// refresh it in place instead of leaving it stale until the user manually
// reloads or navigates away and back.
function refreshIfOnRelevantPage(
  incoming: Notification[],
  pathname: string,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  for (const notification of incoming) {
    const url = notification.action_url;
    if (!url) continue;
    const match = STARTUP_PATH_RE.exec(url);
    if (match && tPath(url) === pathname) {
      void queryClient.invalidateQueries({ queryKey: ['startup', match[1]] });
    }
  }
}

const POLL_INTERVAL_IDLE = 30000;
const POLL_INTERVAL_ACTIVE = 30000;

function hasActiveWork(): boolean {
  const hasActiveTasks = Object.values(useBackgroundTaskStore.getState().tasks)
    .some((t) => t.status === 'polling');
  const isOverlayMinimized = useProcessingOverlayStore.getState().isMinimized;
  return hasActiveTasks || isOverlayMinimized;
}

function getRoleHeader() {
  const user = useAuthStore.getState().user;
  return { headers: { 'X-User-Role': user?.role || '' } };
}

// One app-wide poll timer. NotificationBell (always mounted) and NotificationsPage
// both call useNotifications, and every extra mount — StrictMode double-invoke, route
// change, HMR reload — used to start its own setTimeout chain without killing the
// previous one. Those chains accumulate into overlapping 30s timers that fire ~1/s.
// A single timer plus a generation token (any loop from a superseded generation dies
// instead of re-arming) makes duplication impossible.
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollGeneration = 0;
let pollSubscribers = 0;
let latestPoll: (() => Promise<void>) | null = null;

function runPollLoop(generation: number) {
  const interval = hasActiveWork() ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_IDLE;
  pollTimer = setTimeout(async () => {
    if (generation !== pollGeneration) return;
    if (document.visibilityState === 'visible' && latestPoll) {
      await latestPoll();
    }
    if (generation !== pollGeneration) return;
    runPollLoop(generation);
  }, interval);
}

function ensurePollerRunning() {
  if (pollTimer !== null) return;
  runPollLoop(++pollGeneration);
}

function stopPoller() {
  pollGeneration += 1;
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

interface UseNotificationsOptions {
  /** Narrows the fetched list to one type. The backend owns the filtering, and
   *  the unread count it returns stays global whatever is asked for. */
  type?: NotificationType;
}

export function useNotifications({ type }: UseNotificationsOptions = {}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // The backend refuses /notifications to an account that is not active, and
  // the 30s poll would answer each 403 with a session re-read. The account's
  // own state decides, wherever this hook is mounted — bell or page.
  const accountInactive = useAuthStore((s) => isAccountInactive(s.user));
  // A narrowed list is this caller's alone: the store is the whole inbox, read
  // by the always-mounted bell and fed by the poll, so writing one type into it
  // would empty the bell and letting the poll into it would put off-type
  // arrivals back in front of a caller that asked for one type.
  const [typedNotifications, setTypedNotifications] = useState<Notification[]>([]);
  const location = useLocation();
  const queryClient = useQueryClient();
  const {
    notifications,
    unreadCount,
    isPolling,
    setNotifications,
    processIncoming,
    setUnreadCount,
    markAsRead: markAsReadLocal,
    markAllAsRead: markAllAsReadLocal,
    setLastPollTimestamp,
    setIsPolling,
  } = useNotificationStore();

  // Read fresh at poll time without making pollNotifications (and therefore
  // the poll-scheduling effect) depend on the route — a page navigation
  // shouldn't reset the in-flight poll cadence.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const fetchNotifications = useCallback(async (limit = 20, offset = 0) => {
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (type) params.set('type', type);
      const response = await callApi<NotificationListResponse>(
        `/notifications?${params.toString()}`,
        getRoleHeader(),
      );
      if (type) {
        setTypedNotifications(response.notifications);
      } else {
        setNotifications(response.notifications);
      }
      setUnreadCount(response.unread_count);
      return response;
    } catch {
      return null;
    }
  }, [type, setNotifications, setUnreadCount]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await callApi<UnreadCountResponse>(
        '/notifications/unread-count',
        getRoleHeader(),
      );
      setUnreadCount(response.unread_count);
      return response.unread_count;
    } catch {
      return 0;
    }
  }, [setUnreadCount]);

  const pollNotifications = useCallback(async () => {
    const { isAuthenticated: signedIn, user } = useAuthStore.getState();
    if (!signedIn || isAccountInactive(user) || document.visibilityState !== 'visible') return;

    setIsPolling(true);
    try {
      const lastPollTimestamp = useNotificationStore.getState().lastPollTimestamp;
      const params = lastPollTimestamp ? `?since=${encodeURIComponent(lastPollTimestamp)}` : '';
      const response = await callApi<NotificationPollResponse>(
        `/notifications/poll${params}`,
        getRoleHeader(),
      );

      if (response.has_new && response.notifications.length > 0) {
        refreshIfOnRelevantPage(response.notifications, pathnameRef.current, queryClient);

        const toastWorthy = processIncoming(response.notifications);

        for (const notification of toastWorthy) {
          toast.notification(notification.title, {
            description: notification.message,
            actionLabel: notification.action_url ? i18n.t('common.view') : undefined,
            onAction: notification.action_url
              ? () => openNotificationLink(notification.action_url!)
              : undefined,
            duration: 2000,
            position: 'top-right',
          });
        }
      }

      setUnreadCount(response.unread_count);
      if (response.last_timestamp) {
        setLastPollTimestamp(response.last_timestamp);
      }
    } catch {
      // Network error — will retry next cycle
    } finally {
      setIsPolling(false);
    }
  }, [setIsPolling, processIncoming, setUnreadCount, setLastPollTimestamp, queryClient]);

  const markAsRead = useCallback(async (notificationId: string) => {
    markAsReadLocal(notificationId);
    setTypedNotifications((current) =>
      current.map((n) =>
        n.id === notificationId
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n,
      ),
    );
    try {
      await callApi<MarkReadResponse>(`/notifications/${notificationId}/read`, {
        method: 'PATCH',
        ...getRoleHeader(),
      });
    } catch {
      // Local state is already updated; tolerate transient API failures silently.
    }
  }, [markAsReadLocal]);

  const markAllAsRead = useCallback(async () => {
    markAllAsReadLocal();
    setTypedNotifications((current) =>
      current.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })),
    );
    try {
      await callApi<MarkReadResponse>('/notifications/mark-all-read', {
        method: 'POST',
        ...getRoleHeader(),
      });
    } catch {
      // Local state is already updated; tolerate transient API failures silently.
    }
  }, [markAllAsReadLocal]);

  useEffect(() => {
    if (!isAuthenticated || accountInactive) return;

    latestPoll = pollNotifications;
    fetchNotifications();

    pollSubscribers += 1;
    ensurePollerRunning();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') pollNotifications();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      pollSubscribers -= 1;
      if (pollSubscribers <= 0) {
        pollSubscribers = 0;
        stopPoller();
      }
    };
  }, [isAuthenticated, accountInactive, fetchNotifications, pollNotifications]);

  return {
    notifications: type ? typedNotifications : notifications,
    unreadCount,
    isPolling,
    fetchNotifications,
    fetchUnreadCount,
    pollNotifications,
    markAsRead,
    markAllAsRead,
  };
}
